/**
 * Municipal Bond Date Constants
 *
 * This module provides default date patterns commonly used in municipal bonds
 * as convenience enhancements. These are NOT requirements - users can override
 * with any dates they want. The processing code should work with whatever dates
 * are provided and should not assume these specific patterns.
 *
 * Common Pattern (Michigan Municipal Bonds):
 * - Fiscal Year End: June 30th
 * - Payment Dates: November 1st (70% revenue) and May 1st (30% revenue)
 * - Interest-only on November 1st, Principal + Interest on May 1st
 */

import type { DateString } from '~/types';

/**
 * Default fiscal year patterns for municipal bonds
 * Pattern depends on tax collection schedule
 */
export const MUNICIPAL_FISCAL_YEAR = {
  SUMMER: {
    /** Month of fiscal year end (June = 6) */
    END_MONTH: 6,
    /** Day of fiscal year end (30th) */
    END_DAY: 30,
    /** Fiscal year ends in the year after the tax year starts */
    YEAR_OFFSET: 1,
  },
  WINTER: {
    /** Month of fiscal year end (November = 11) */
    END_MONTH: 11,
    /** Day of fiscal year end (1st) */
    END_DAY: 1,
    /** Fiscal year ends in the year after the tax year starts */
    YEAR_OFFSET: 1,
  },
} as const;

/**
 * Common payment date patterns for municipal bonds
 */
export const MUNICIPAL_PAYMENT_DATES = {
  /** November 1st payment (typically interest-only) */
  NOVEMBER: {
    MONTH: 11,
    DAY: 1,
    /** Common revenue collection percentage for November payment */
    DEFAULT_REVENUE_PERCENTAGE: 70,
    /** Whether this is typically interest-only */
    DEFAULT_IS_INTEREST_ONLY: true,
  },

  /** May 1st payment (typically principal + interest) */
  MAY: {
    MONTH: 5,
    DAY: 1,
    /** Common revenue collection percentage for May payment */
    DEFAULT_REVENUE_PERCENTAGE: 30,
    /** Whether this is typically interest-only */
    DEFAULT_IS_INTEREST_ONLY: false,
    /** Year offset from tax year (May is in the following calendar year) */
    YEAR_OFFSET: 1,
  },
} as const;

/**
 * Helper function to generate default fiscal year end date
 * This is a convenience function - users can specify any fiscal year end
 *
 * @param taxYear - The starting tax year
 * @param schedule - Tax collection schedule ("summer" or "winter"). Defaults to "summer" for backward compatibility.
 * @returns The fiscal year end date in YYYY-MM-DD format
 *
 * Examples:
 * - Summer (Tax Year 2025): Returns "2026-06-30"
 * - Winter (Tax Year 2025): Returns "2026-11-01"
 */
export const getDefaultFiscalYearEnd = (
  taxYear: number,
  schedule: 'summer' | 'winter' = 'summer'
): DateString => {
  const pattern =
    schedule === 'winter' ? MUNICIPAL_FISCAL_YEAR.WINTER : MUNICIPAL_FISCAL_YEAR.SUMMER;
  const year = taxYear + pattern.YEAR_OFFSET;
  const month = String(pattern.END_MONTH).padStart(2, '0');
  const day = String(pattern.END_DAY).padStart(2, '0');
  return `${year}-${month}-${day}` as DateString;
};

/**
 * Helper function to generate common municipal bond payment dates
 * This is a convenience function - users can specify any payment dates
 *
 * @param taxYear - The starting tax year
 * @returns Array of payment date configurations
 */
export const getDefaultPaymentDates = (taxYear: number) => {
  const novemberDate = `${taxYear}-${String(MUNICIPAL_PAYMENT_DATES.NOVEMBER.MONTH).padStart(2, '0')}-${String(MUNICIPAL_PAYMENT_DATES.NOVEMBER.DAY).padStart(2, '0')}`;
  const mayDate = `${taxYear + MUNICIPAL_PAYMENT_DATES.MAY.YEAR_OFFSET}-${String(MUNICIPAL_PAYMENT_DATES.MAY.MONTH).padStart(2, '0')}-${String(MUNICIPAL_PAYMENT_DATES.MAY.DAY).padStart(2, '0')}`;

  return [
    {
      date: novemberDate,
      revenuePercentage: MUNICIPAL_PAYMENT_DATES.NOVEMBER.DEFAULT_REVENUE_PERCENTAGE,
      isInterestOnly: MUNICIPAL_PAYMENT_DATES.NOVEMBER.DEFAULT_IS_INTEREST_ONLY,
    },
    {
      date: mayDate,
      revenuePercentage: MUNICIPAL_PAYMENT_DATES.MAY.DEFAULT_REVENUE_PERCENTAGE,
      isInterestOnly: MUNICIPAL_PAYMENT_DATES.MAY.DEFAULT_IS_INTEREST_ONLY,
    },
  ];
};

/**
 * Alternative payment frequencies that might be used
 * These are for future expansion - not currently implemented
 */
export const ALTERNATIVE_PAYMENT_FREQUENCIES = {
  QUARTERLY: 4, // 4 payments per year
  BIANNUAL: 2, // 2 payments per year (current default)
  TRIANNUAL: 3, // 3 payments per year
  BIMONTHLY: 6, // 6 payments per year
} as const;

/**
 * Validation constants for date inputs
 */
export const DATE_VALIDATION = {
  /** Minimum allowed tax year */
  MIN_TAX_YEAR: 2000,

  /** Maximum allowed tax year */
  MAX_TAX_YEAR: 2150,

  /** Maximum number of payment dates per year */
  MAX_PAYMENT_DATES_PER_YEAR: 12,

  /** Tolerance for revenue percentage sum (must equal 100% ± tolerance) */
  REVENUE_PERCENTAGE_TOLERANCE: 0.01,
} as const;
